import type { SearchClubItemDto } from '@customTypes/types.ts';
import { useIntersection } from '@mantine/hooks';
import { keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { useSearchClubs } from '@/shared/api/requests';
import { useIsLoading } from '@/shared/hooks/useIsLoading';

export const useArtistSearchPage = () => {
  const [originalClubList, setOriginalClubList] = useState<SearchClubItemDto[]>(
    []
  );

  const search = useSearch({ from: '/artist/$id/_artist/search' });
  const navigate = useNavigate({ from: '/artist/$id/search' });

  const countries = useMemo(() => {
    if (typeof search?.countries === 'string') {
      return search.countries.split(',').filter(Boolean);
    }
    return [];
  }, [search?.countries]);

  const setCountries = (newValues: string[]) => {
    navigate({
      search: {
        countries: newValues.length > 0 ? newValues.join(',') : undefined
      }
    });
  };

  const { ref: elementRef, entry } = useIntersection();

  const countryParams = useMemo(
    () => (countries.length > 1 ? countries.join(',') : countries[0]),
    [countries]
  );

  const clubs = useSearchClubs(
    {
      countries: countryParams
    },
    {
      options: {
        placeholderData: keepPreviousData
      }
    }
  );

  useEffect(() => {
    if (entry?.isIntersecting) {
      clubs.fetchNextPage();
    }
  }, [entry?.isIntersecting]);

  const posts = useMemo(
    () => clubs.data?.pages.flatMap((page) => page.data.items) || [],
    [clubs.data?.pages]
  );

  useEffect(() => {
    if (countries.length === 0 && posts.length > 0) {
      setOriginalClubList((prev) => [...prev, ...posts]);
    }
  }, [countries, posts]);

  // сортировка стран в которых есть клубы
  const availableCountries = useMemo(() => {
    const countrySet = new Set<string>();
    originalClubList.forEach((club) => {
      if (club.country) {
        countrySet.add(club.country);
      }
    });
    return Array.from(countrySet).sort(); // сортируем по алфавиту
  }, [originalClubList]);

  const isLoading = useIsLoading([clubs]);

  return {
    data: posts,
    state: {
      isLoading,
      country: countries,
      originalClubList,
      availableCountries,
      setCountries,
      elementRef
    },
    functions: {
      navigate
    }
  };
};
